import { RiEyeLine, RiEyeOffLine, RiLoader4Line } from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { fetchClient } from "@/api/client";
import { $api } from "@/api/query";
import { CatalogueResults } from "@/components/integrations/catalogue-results";
import { FarfetchOauthClients } from "@/components/integrations/farfetch-oauth-clients";
import { FulfillmentSettings } from "@/components/integrations/fulfillment-settings";
import { PriceResults } from "@/components/integrations/price-results";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fetchCurrentUser } from "@/lib/auth";
import { isOrgAdmin } from "@/lib/role";

/// `tab` lets a link land on the right tab, and `report` pre-expands one
/// catalogue-ingest report. Both exist because a notification about a rejected
/// catalogue used to link at `/integrations/farfetch` with no tab, so it opened
/// on Credentials and the operator had to know to click through to Exports and
/// find the report by hand.
const searchSchema = z.object({
	tab: z.enum(["credentials", "export", "orders", "fulfillment"]).optional(),
	report: z.string().optional(),
	/// Pre-expands one PRICING report (a `farfetch_price_rejected` notification).
	priceReport: z.string().optional(),
});

export const Route = createFileRoute("/_app/integrations/farfetch")({
	validateSearch: searchSchema,
	// System page: org members are bounced to the dashboard (same pattern as
	// /admin). The backend enforces OrgAdmin on the config endpoints too.
	beforeLoad: async () => {
		const user = await fetchCurrentUser();
		if (!isOrgAdmin(user)) {
			throw redirect({ to: "/dashboard" });
		}
	},
	component: FarfetchIntegrationPage,
});

const filledClass = "border-primary/50 bg-primary/5";

function FilledInput({
	filled,
	...props
}: React.ComponentProps<typeof Input> & { filled?: boolean }) {
	return <Input {...props} className={filled ? filledClass : undefined} />;
}

function PasswordInput({
	filled,
	...props
}: React.ComponentProps<typeof Input> & { filled?: boolean }) {
	const [visible, setVisible] = useState(false);
	return (
		<div className="relative">
			<Input
				{...props}
				type={visible ? "text" : "password"}
				className={`pr-9 ${filled ? filledClass : ""}`}
			/>
			<button
				type="button"
				onClick={() => setVisible(!visible)}
				className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
			>
				{visible ? <RiEyeOffLine className="size-4" /> : <RiEyeLine className="size-4" />}
			</button>
		</div>
	);
}

interface FarfetchConfig {
	farfetchSftpHost?: string | null;
	farfetchSftpPort?: number;
	farfetchSftpUser?: string | null;
	farfetchSftpPassword?: string | null;
	farfetchSftpKeyPath?: string | null;
	// migration 042
	farfetchSftpHostKeyFingerprint?: string | null;
	farfetchCatalogueExportDir?: string | null;
	farfetchStockExportDir?: string | null;
	farfetchResultDir?: string | null;
	farfetchPriceResultDir?: string | null;
	farfetchOrderImportDir?: string | null;
	farfetchExportEnabled?: boolean;
	farfetchExportIntervalHours?: number;
	lastFarfetchExportAt?: string | null;
	// migration 024
	farfetchOrderArchiveDir?: string | null;
	farfetchOrderPollEnabled?: boolean;
	farfetchOrderPollIntervalMinutes?: number;
	// migration 040
	farfetchSiteId?: string | null;
	farfetchStockScheduleEnabled?: boolean;
	farfetchStockDeltaEnabled?: boolean;
	farfetchStockScheduleIntervalHours?: number;
	lastFarfetchStockExportAt?: string | null;
}

const EXPORT_INTERVAL_OPTIONS = [
	{ value: 1, label: "Every hour" },
	{ value: 3, label: "Every 3 hours" },
	{ value: 6, label: "Every 6 hours" },
	{ value: 12, label: "Every 12 hours (default)" },
	{ value: 24, label: "Daily" },
	{ value: 48, label: "Every 2 days" },
	{ value: 168, label: "Weekly" },
];

const POLL_INTERVAL_OPTIONS = [
	{ value: 1, label: "Every minute" },
	{ value: 5, label: "Every 5 min (default)" },
	{ value: 15, label: "Every 15 min" },
	{ value: 30, label: "Every 30 min" },
	{ value: 60, label: "Every hour" },
];

function FarfetchIntegrationPage() {
	const queryClient = useQueryClient();
	const search = useSearch({ from: "/_app/integrations/farfetch" });
	// Seeded from the URL so a deep link opens the right tab, then held in
	// state so clicking a tab afterwards does not need a navigation.
	const [tab, setTab] = useState(search.tab ?? "credentials");
	const { data, isLoading } = $api.useQuery("get", "/api/v1/integrations/config");
	const config = data as FarfetchConfig | undefined;

	// Whether the org has a primary identifier column configured (Matching
	// page). The stock feed needs one to key rows against - the scheduler
	// backend gates on the same `primary_identifier_column` column - so the
	// schedule switch here stays disabled until one is set.
	const { data: targetColumnsData } = $api.useQuery(
		"get",
		"/api/v1/catalogue/target-columns" as never,
		{ params: { query: { target: "farfetch" } } } as never,
	);
	const hasPrimaryIdentifier = (
		(targetColumnsData as Array<{ isPrimaryIdentifier: boolean }> | undefined) ?? []
	).some((c) => c.isPrimaryIdentifier);

	const [sftpHost, setSftpHost] = useState("");
	const [sftpPort, setSftpPort] = useState(22);
	const [sftpUser, setSftpUser] = useState("");
	const [sftpPassword, setSftpPassword] = useState("");
	const [keyPath, setKeyPath] = useState("");
	const [hostKeyFingerprint, setHostKeyFingerprint] = useState("");
	const [catalogueDir, setCatalogueDir] = useState("");
	const [stockDir, setStockDir] = useState("");
	const [resultDir, setResultDir] = useState("");
	const [priceResultDir, setPriceResultDir] = useState("");
	const [orderDir, setOrderDir] = useState("");
	const [archiveDir, setArchiveDir] = useState("");
	const [exportEnabled, setExportEnabled] = useState(false);
	const [exportIntervalHours, setExportIntervalHours] = useState(12);
	const [pollEnabled, setPollEnabled] = useState(false);
	const [pollIntervalMinutes, setPollIntervalMinutes] = useState(5);
	const [siteId, setSiteId] = useState("");
	const [stockScheduleEnabled, setStockScheduleEnabled] = useState(false);
	const [stockDeltaEnabled, setStockDeltaEnabled] = useState(false);
	const [stockScheduleIntervalHours, setStockScheduleIntervalHours] = useState(24);
	const [saving, setSaving] = useState(false);
	const [testing, setTesting] = useState(false);
	const [polling, setPolling] = useState(false);
	const [exportingStock, setExportingStock] = useState(false);

	useEffect(() => {
		if (!config) return;
		setSftpHost(config.farfetchSftpHost ?? "");
		setSftpPort(config.farfetchSftpPort ?? 22);
		setSftpUser(config.farfetchSftpUser ?? "");
		setSftpPassword(config.farfetchSftpPassword ?? "");
		setKeyPath(config.farfetchSftpKeyPath ?? "");
		setHostKeyFingerprint(config.farfetchSftpHostKeyFingerprint ?? "");
		setCatalogueDir(config.farfetchCatalogueExportDir ?? "");
		setStockDir(config.farfetchStockExportDir ?? "");
		setResultDir(config.farfetchResultDir ?? "");
		setPriceResultDir(config.farfetchPriceResultDir ?? "");
		setOrderDir(config.farfetchOrderImportDir ?? "");
		setArchiveDir(config.farfetchOrderArchiveDir ?? "");
		setExportEnabled(config.farfetchExportEnabled ?? false);
		setExportIntervalHours(config.farfetchExportIntervalHours ?? 12);
		setPollEnabled(config.farfetchOrderPollEnabled ?? false);
		setPollIntervalMinutes(config.farfetchOrderPollIntervalMinutes ?? 5);
		setSiteId(config.farfetchSiteId ?? "");
		setStockScheduleEnabled(config.farfetchStockScheduleEnabled ?? false);
		setStockDeltaEnabled(config.farfetchStockDeltaEnabled ?? false);
		setStockScheduleIntervalHours(config.farfetchStockScheduleIntervalHours ?? 24);
	}, [config]);

	function invalidate() {
		queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/integrations/config"] });
	}

	async function handleSave() {
		// Never save off a form that was hydrated from a failed config GET:
		// the dir fields send raw strings ("" clears), so saving the blank
		// form would wipe all four directory settings.
		if (!config) {
			toast.error("Config not loaded - refresh before saving");
			return;
		}
		setSaving(true);
		try {
			const body: Record<string, unknown> = {
				farfetchSftpHost: sftpHost || null,
				farfetchSftpPort: sftpPort,
				farfetchSftpUser: sftpUser || null,
				farfetchSftpPassword: sftpPassword || null,
				farfetchSftpKeyPath: keyPath || null,
				// Raw string, no `|| null` coercion: "" must reach the backend to
				// clear the column (disable pinning), same as the dir fields.
				farfetchSftpHostKeyFingerprint: hostKeyFingerprint,
				farfetchCatalogueExportDir: catalogueDir,
				farfetchStockExportDir: stockDir,
				farfetchResultDir: resultDir,
				farfetchPriceResultDir: priceResultDir,
				farfetchOrderImportDir: orderDir,
				farfetchOrderArchiveDir: archiveDir,
				farfetchExportEnabled: exportEnabled,
				farfetchExportIntervalHours: exportIntervalHours,
				farfetchOrderPollEnabled: pollEnabled,
				farfetchOrderPollIntervalMinutes: pollIntervalMinutes,
				farfetchSiteId: siteId || null,
				farfetchStockScheduleEnabled: stockScheduleEnabled,
				farfetchStockDeltaEnabled: stockDeltaEnabled,
				farfetchStockScheduleIntervalHours: stockScheduleIntervalHours,
			};
			const { error } = await fetchClient.PUT(
				"/api/v1/integrations/config" as never,
				{ body } as never,
			);
			if (error) {
				toast.error("Failed to save Farfetch config");
				return;
			}
			toast.success("Farfetch config saved");
			invalidate();
		} finally {
			setSaving(false);
		}
	}

	async function handleTest() {
		setTesting(true);
		try {
			const { data: result, error } = await fetchClient.POST(
				"/api/v1/integrations/test-farfetch" as never,
			);
			if (error) {
				toast.error("Test request failed");
				return;
			}
			const r = result as { success: boolean; message: string };
			if (r.success) toast.success(r.message);
			else toast.error(r.message);
		} finally {
			setTesting(false);
		}
	}

	async function handleExportNow() {
		if (exportingStock) return;
		setExportingStock(true);
		try {
			const { error } = await fetchClient.POST("/api/v1/farfetch/stock-export" as never);
			if (error) {
				toast.error("Export failed");
				return;
			}
			toast.success("Stock export triggered");
		} finally {
			setExportingStock(false);
		}
	}

	async function handlePollNow() {
		setPolling(true);
		try {
			const { data: result, error } = await fetchClient.POST(
				"/api/v1/farfetch/orders/poll-now" as never,
			);
			if (error) {
				toast.error("Poll failed (already running, or SFTP not configured)");
				return;
			}
			const r = result as { message: string };
			toast.success(r.message);
			queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/farfetch/orders"] });
		} finally {
			setPolling(false);
		}
	}

	return (
		<div className="space-y-4">
			<div>
				<h1 className="text-2xl font-bold tracking-tight">Farfetch</h1>
				<p className="text-muted-foreground">
					SFTP credentials, catalogue export delivery, order import, and site mapping.
				</p>
			</div>

			{isLoading ? (
				<div className="flex items-center gap-2 text-muted-foreground">
					<RiLoader4Line className="size-4 animate-spin" />
					Loading configuration...
				</div>
			) : (
				<Tabs value={tab} onValueChange={(v) => v && setTab(v as typeof tab)} className="max-w-2xl">
					<TabsList>
						<TabsTrigger value="credentials">Credentials</TabsTrigger>
						<TabsTrigger value="export">Exports</TabsTrigger>
						<TabsTrigger value="orders">Order Import</TabsTrigger>
						<TabsTrigger value="fulfillment">Fulfillment</TabsTrigger>
					</TabsList>

					<TabsContent value="credentials">
						<Card>
							<CardHeader>
								<CardTitle>SFTP credentials</CardTitle>
								<CardDescription>
									Use either a password (typical for boutique SFTPs) or a private-key path on the
									server.
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="grid grid-cols-[1fr_100px] gap-2">
									<div className="space-y-2">
										<Label htmlFor="ff-host">SFTP Host</Label>
										<FilledInput
											id="ff-host"
											value={sftpHost}
											onChange={(e) => setSftpHost(e.target.value)}
											placeholder="sftp.farfetch.com"
											filled={!!sftpHost}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="ff-port">Port</Label>
										<FilledInput
											id="ff-port"
											type="number"
											min={1}
											max={65535}
											value={sftpPort}
											onChange={(e) => setSftpPort(Number(e.target.value) || 22)}
											filled={sftpPort !== 22}
										/>
									</div>
								</div>
								<div className="space-y-2">
									<Label htmlFor="ff-user">SFTP User</Label>
									<FilledInput
										id="ff-user"
										value={sftpUser}
										onChange={(e) => setSftpUser(e.target.value)}
										placeholder="username"
										filled={!!sftpUser}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="ff-password">Password</Label>
									<PasswordInput
										id="ff-password"
										value={sftpPassword}
										onChange={(e) => setSftpPassword(e.target.value)}
										placeholder="(leave **** to keep existing)"
										filled={!!sftpPassword && sftpPassword !== "****"}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="ff-key">Key Path (alternative)</Label>
									<FilledInput
										id="ff-key"
										value={keyPath}
										onChange={(e) => setKeyPath(e.target.value)}
										placeholder="/path/to/private_key (only if no password)"
										filled={!!keyPath}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="ff-hostkey">Host Key Fingerprint</Label>
									<FilledInput
										id="ff-hostkey"
										value={hostKeyFingerprint}
										onChange={(e) => setHostKeyFingerprint(e.target.value)}
										placeholder="SHA256:abc...,SHA256:def..."
										filled={!!hostKeyFingerprint}
									/>
									<p className="text-sm text-muted-foreground">
										Comma-separated OpenSSH SHA256 fingerprints of the SFTP host key. Leave empty to
										disable pinning (any host key is accepted). Obtain with{" "}
										<code>ssh-keyscan -T 15 &lt;host&gt; | ssh-keygen -lf -</code>
									</p>
								</div>
								<div className="flex gap-2 pt-2">
									<Button onClick={handleSave} disabled={saving}>
										{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
										Save
									</Button>
									<Button variant="outline" onClick={handleTest} disabled={testing}>
										{testing ? <RiLoader4Line className="mr-1 size-4 animate-spin" /> : null}
										Test connection
									</Button>
								</div>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="export" className="space-y-4">
						<Card>
							<CardHeader>
								<CardTitle>Catalogue export</CardTitle>
								<CardDescription>
									Full Master Catalogue CSV, delivered on the schedule below. Manual generation
									lives under Catalogue -{">"} Farfetch. Column mapping lives under Catalogue -{">"}{" "}
									Matching.
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="ff-catalogue">Catalogue Export Directory</Label>
									<FilledInput
										id="ff-catalogue"
										value={catalogueDir}
										onChange={(e) => setCatalogueDir(e.target.value)}
										placeholder="/productCatalogue/inbound"
										filled={!!catalogueDir}
									/>
								</div>
								<div className="flex items-center justify-between">
									<Label className="font-medium">Scheduled export</Label>
									<Switch checked={exportEnabled} onCheckedChange={setExportEnabled} />
								</div>
								<div className="space-y-2">
									<Label>Interval</Label>
									<Select
										value={String(exportIntervalHours)}
										onValueChange={(v) => setExportIntervalHours(Number(v))}
										disabled={!exportEnabled}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{EXPORT_INTERVAL_OPTIONS.map((o) => (
												<SelectItem key={o.value} value={String(o.value)}>
													{o.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="flex gap-2 pt-2">
									<Button onClick={handleSave} disabled={saving}>
										{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
										Save
									</Button>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Stock export</CardTitle>
								<CardDescription>
									Separate stock/inventory feed - distinct from the full catalogue, delivered on the
									schedule below. Requires a primary identifier column (set in Catalogue -{">"}{" "}
									Matching) so each row can be keyed correctly.
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="ff-stock">Stock Export Directory</Label>
									<FilledInput
										id="ff-stock"
										value={stockDir}
										onChange={(e) => setStockDir(e.target.value)}
										placeholder="/inventory/inbound"
										filled={!!stockDir}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="ff-site-id">Site ID</Label>
									<FilledInput
										id="ff-site-id"
										value={siteId}
										onChange={(e) => setSiteId(e.target.value)}
										placeholder="Farfetch site ID"
										filled={!!siteId}
									/>
								</div>
								<div className="flex items-center justify-between">
									<Label htmlFor="ff-stock-schedule" className="font-medium">
										Scheduled stock sync
									</Label>
									<Switch
										id="ff-stock-schedule"
										checked={stockScheduleEnabled}
										onCheckedChange={setStockScheduleEnabled}
										disabled={!hasPrimaryIdentifier}
									/>
								</div>
								{!hasPrimaryIdentifier && (
									<p className="text-sm text-muted-foreground">
										Set a primary key in Matching first
									</p>
								)}
								<div className="flex items-center justify-between">
									<Label htmlFor="ff-stock-delta" className="font-medium">
										Real-time stock deltas (IF1010)
									</Label>
									<Switch
										id="ff-stock-delta"
										checked={stockDeltaEnabled}
										onCheckedChange={setStockDeltaEnabled}
										disabled={!hasPrimaryIdentifier}
									/>
								</div>
								<p className="text-sm text-muted-foreground">
									Pushes each stock change to Farfetch within a minute. The first run only records
									what the daily file already told Farfetch; nothing is re-sent.
								</p>
								<div className="space-y-2">
									<Label>Interval</Label>
									<Select
										value={String(stockScheduleIntervalHours)}
										onValueChange={(v) => setStockScheduleIntervalHours(Number(v))}
										disabled={!stockScheduleEnabled || !hasPrimaryIdentifier}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{EXPORT_INTERVAL_OPTIONS.map((o) => (
												<SelectItem key={o.value} value={String(o.value)}>
													{o.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="flex gap-2 pt-2">
									<Button onClick={handleSave} disabled={saving}>
										{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
										Save
									</Button>
									<Button variant="outline" onClick={handleExportNow} disabled={exportingStock}>
										{exportingStock && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
										Export stock now
									</Button>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Catalogue results</CardTitle>
								<CardDescription>
									Where Farfetch drops post-upload ingest reports for the catalogue feed, and the
									reports themselves once ingested.
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="ff-result">Result Directory</Label>
									<FilledInput
										id="ff-result"
										value={resultDir}
										onChange={(e) => setResultDir(e.target.value)}
										placeholder="/productCatalogue/outbound/result"
										filled={!!resultDir}
									/>
								</div>
								<div className="flex gap-2 pt-2">
									<Button onClick={handleSave} disabled={saving}>
										{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
										Save
									</Button>
								</div>
								<CatalogueResults initialReportId={search.report} />
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Price results</CardTitle>
								<CardDescription>
									Where Farfetch drops its daily pricing reports, and the reports themselves. A
									rejected line is a price Farfetch never applied - it is still selling at the old
									price.
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="ff-price-result">Price Result Directory</Label>
									<FilledInput
										id="ff-price-result"
										value={priceResultDir}
										onChange={(e) => setPriceResultDir(e.target.value)}
										placeholder="/pricing/outbound/result"
										filled={!!priceResultDir}
									/>
								</div>
								<div className="flex gap-2 pt-2">
									<Button onClick={handleSave} disabled={saving}>
										{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
										Save
									</Button>
								</div>
								<PriceResults initialReportId={search.priceReport} />
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="orders" className="space-y-4">
						<Card>
							<CardHeader>
								<CardTitle>Order import</CardTitle>
								<CardDescription>
									Where Farfetch drops order files and how often we poll. Processed files move to
									the archive directory.
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="ff-order">Order Import Directory</Label>
									<FilledInput
										id="ff-order"
										value={orderDir}
										onChange={(e) => setOrderDir(e.target.value)}
										placeholder="/oms/outbound"
										filled={!!orderDir}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="ff-archive">Archive Directory</Label>
									<FilledInput
										id="ff-archive"
										value={archiveDir}
										onChange={(e) => setArchiveDir(e.target.value)}
										placeholder="/oms/archive"
										filled={!!archiveDir}
									/>
								</div>
								<div className="flex items-center justify-between">
									<Label className="font-medium">Poll for new orders</Label>
									<Switch checked={pollEnabled} onCheckedChange={setPollEnabled} />
								</div>
								<div className="space-y-2">
									<Label>Poll interval</Label>
									<Select
										value={String(pollIntervalMinutes)}
										onValueChange={(v) => setPollIntervalMinutes(Number(v))}
										disabled={!pollEnabled}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{POLL_INTERVAL_OPTIONS.map((o) => (
												<SelectItem key={o.value} value={String(o.value)}>
													{o.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="flex gap-2 pt-2">
									<Button onClick={handleSave} disabled={saving}>
										{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
										Save
									</Button>
									<Button variant="outline" onClick={handlePollNow} disabled={polling}>
										{polling ? <RiLoader4Line className="mr-1 size-4 animate-spin" /> : null}
										Poll now
									</Button>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Order events (push)</CardTitle>
								<CardDescription>
									Machine OAuth clients for Farfetch's EVENTS push model - an alternative to
									polling. Register a client, hand Farfetch the secret and URLs shown once, and
									Farfetch calls our webhook directly as orders change.
								</CardDescription>
							</CardHeader>
							<CardContent>
								<FarfetchOauthClients />
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="fulfillment">
						<Card>
							<CardHeader>
								<CardTitle>Fulfillment</CardTitle>
								<CardDescription>
									Choose how Burresi picks the shipping branch for incoming orders from its own
									stock. Farfetch's siteID is not used for routing.
								</CardDescription>
							</CardHeader>
							<CardContent>
								<FulfillmentSettings />
							</CardContent>
						</Card>
					</TabsContent>
				</Tabs>
			)}
		</div>
	);
}
